import { dataGet } from "../dot"

/**
 * Gets an array of rules from the folder `./rules`
 * 
 * This will require any file in there so make sure to either change require.context
 * or add helpers in a different folder
 * 
 * See any of the rules for the required format
 * 
 * @returns {Object}
 */
const getRules = () => {
    const requireContext = require.context('./rules/', true, /\.js$/)

    return requireContext.keys()
        .reduce((rules, ruleFile) => {
            //Import the file and take the exported value
            const rule = requireContext(ruleFile).default

            return {
                ...rules,
                [rule.name]: rule
            }
        }, {})
}

/**
 * Contains a list of all validatable rules
 */
const availableRules = getRules()

class Validator {
    /**
     * @param {Object<String, Array>} rules An object key'd by field name, containing all the rules to validate for that field
     * @param {Object<String, *>} values    An object key'd by field name, containing the values for the rules to validate against
     * @param {Boolean} logToConsole        Whether failed rules should output an error in the console
     */
    constructor(rules = {}, values = {}, logToConsole = false)
    {
        this.rules        = rules
        this.values       = values
        this.logToConsole = logToConsole
        this.errors       = {}

        this.availableRules = availableRules
    }

    /**
     * Run the given validation and return if successful or not
     * 
     * @returns {Boolean}
     */
    validate()
    {
        return Object.keys(this.rules)
            .reduce(
                (success, field) => this.validateField(field) && success, 
                true
            )
    }

    /**
     * Validates a given field
     * 
     * @param {String}           field 
     * @param {String|undefined} root
     * @returns {Boolean}
     */
    validateField(field, root = undefined)
    {
        //If the field contains an asterisk we should validate all of its subfields
        if(field.includes('.*')){
            return this.validateMultiField(field, root ?? field)
        }

        let rules = this.rules[root ?? field]

        //Nullable is a special rule which allows validation to be successful even if other rules fail
        if(rules.includes('nullable')){
            if(this.validateFieldForRule(field, 'nullable', false)){
                return true
            }

            rules = rules.filter(rule => rule !== 'nullable')
        }

        return rules.reduce(
            (success, rule) => this.validateFieldForRule(field, rule) && success,
            true
        )
    }

    /**
     * Validates fields with asterisks in the name
     * 
     * @param {String} field 
     * @param {String} root 
     * @returns {Boolean}
     */
    validateMultiField(field, root){
        const [fieldBeforeAsterisk, _, ...fieldAfterAsterisk] = field.split('.*')

        const data = dataGet(
            fieldBeforeAsterisk,
            this.values
        )

        return Object.keys(data ?? [])
            .reduce(
                (success, index) => this.validateField(`${fieldBeforeAsterisk}.${index}${fieldAfterAsterisk.join('.*')}`, root) && success, 
                true
            )
    }

    /**
     * Runs a given rule against a field
     * 
     * @param {String}  field 
     * @param {String}  ruleName 
     * @param {Boolean} withError
     * @returns {Boolean}
     */
    validateFieldForRule(field, ruleName, withError = true)
    {
        const { rule, parameters } = this.getRuleAndParameters(ruleName)

        const ruleToRun = this.availableRules[rule]
        const value     = dataGet(field, this.values)

        //If we don't have a defined rule we should always raise a console error
        //but assume it's an invalid field so it isn't just ignored
        if(ruleToRun === undefined){
            console.error(`The rule ${rule} doesn't exist`)

            return false
        }

        //If we don't have a valid field
        if(!ruleToRun.validate(value, this.values, parameters)){
            //Get the error message from the rule itself
            const error = ruleToRun.message(field, this.values, parameters)

            //Raise the console error if its enabled
            if(this.logToConsole){
                console.error(error)
            }

            if(withError){
                //Append the message to the root incase we want to display them
                this.errors[field] = [
                    ...this.errors[field] ?? [],
                    error
                ]
            }

            return false
        }

        return true
    }

    /**
     * Gets the rule and parameters from the rule string 
     * 
     * @param {String} rule 
     * @returns {Object<rule: String, parameters: Array>}
     */
    getRuleAndParameters(rule){
        const [ruleName, ...parameters] = rule.split(':')

        return {
            rule       : ruleName,
            //There shouldn't be more colons than the first one, but just incase we should join it again rather than take the first item
            parameters : parameters.join(':').split(',')
        }
    }
}

/**
 * Helper function to create a validator instance
 * 
 * @param {Object} rules 
 * @param {Object} values 
 * @param {Boolean} logToConsole 
 * @returns {Validator}
 */
const createValidator = (rules = {}, values = {}, logToConsole = false) => new Validator(rules, values, logToConsole)

export default Validator

export {
    createValidator
}